import { useState, useEffect, useMemo } from 'react';
import type { UnifiedAccountComponents } from 'unified-auth-core-lib';
import type { AuthProviderKey, AuthRegistry } from './registry';
import { defaultRegistry } from './defaultRegistry';

export function useResolvedProvider(
  appId: number,
  providerKey?: AuthProviderKey,
  registry: AuthRegistry = defaultRegistry
): { components: UnifiedAccountComponents | null; isLoading: boolean } {
  const providerType = useMemo(
    () => registry.getProviderType(appId, providerKey),
    [appId, providerKey, registry]
  );

  const syncComponents = useMemo(() => {
    if (providerType !== 'sync') return null;
    return registry.getComponentsSync(appId, providerKey) ?? null;
  }, [providerType, appId, providerKey, registry]);

  const [components, setComponents] = useState<UnifiedAccountComponents | null>(
    syncComponents
  );
  const [isLoading, setIsLoading] = useState(providerType === 'async');

  useEffect(() => {
    if (providerType === 'sync') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setComponents(syncComponents);
      setIsLoading(false);
      return;
    }

    if (providerType === 'none') {
      setComponents(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setComponents(null);

    registry
      .resolveComponents(appId, providerKey)
      .then((resolved) => {
        if (!cancelled) {
          setComponents(resolved ?? null);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [providerType, syncComponents, appId, providerKey, registry]);

  return { components, isLoading };
}
